import { useEffect, useRef, useState } from 'react';
import { activeChatMediaDrag, CHAT_MEDIA_DRAG_TYPE, readChatMediaDrag } from '../../models/chat/chatMediaDragModel';
import { isImageDrag, isImageFile, MAX_QUERY_IMAGES, resolvePartsDrop, partsDropReason } from '../../models/queries/partsDropModel';
import { blobToDataUrl, downloadChatAttachment } from '../../models/shared/mediaModel';
import { imageIdentity } from '../../../../features/query-photos/models/photoTags';

export function usePartsDropOverlay({ panelRef, form, setField, canManageParts, disabled, onError }) {
  const [drag, setDrag] = useState(null), [zIndex, setZIndex] = useState(1200);
  const current = useRef({ form, setField, canManageParts, disabled, onError }); current.current = { form, setField, canManageParts, disabled, onError };
  const timer = useRef(null), generation = useRef(0), reservations = useRef(new Set());
  const close = () => { clearTimeout(timer.current); timer.current = null; setDrag(null); };
  const add = async (files, payload, target) => {
    const epoch = generation.current;
    const incoming = payload ? [payload] : files.filter(isImageFile);
    for (const item of incoming) {
      const vm = current.current;
      if (vm.disabled || epoch !== generation.current) return;
      const count = vm.form.images.length + [...reservations.current].filter(id => !vm.form.images.some(image => imageIdentity(image) === id)).length;
      if (count >= MAX_QUERY_IMAGES) { vm.onError('A query can have up to 10 photos.'); break; }
      const id = `upload-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const image = { id, source: payload ? 'chat' : 'upload', ...(payload ? { attachmentId: payload.attachmentId, previewPending: true } : { file: item }), name: payload?.filename || item.name, uploadStatus: 'loading' };
      reservations.current.add(id);
      vm.setField('images', images => images.length < MAX_QUERY_IMAGES ? [...images, image] : images);
      if (target !== 'general') vm.setField('tags', tags => resolvePartsDrop({ ...current.current.form, images: [image], tags }, { kind: 'local', image: id, source: null }, target).tags);
      try {
        const file = payload ? (await downloadChatAttachment(payload.attachmentId)).blob : item;
        const previewUrl = await blobToDataUrl(file);
        if (epoch === generation.current) current.current.setField('images', images => images.map(value => value.id === id ? { ...value, source: 'upload', file, previewUrl, previewPending: false, uploadStatus: undefined } : value));
      } catch {
        if (epoch === generation.current) current.current.setField('images', images => images.map(value => value.id === id ? { ...value, previewPending: true, uploadStatus: 'error' } : value));
      } finally { reservations.current.delete(id); }
    }
  };
  const drop = (event, target, suppliedDrag = drag) => {
    event.preventDefault(); event.stopPropagation();
    const vm = current.current, copy = Boolean(event.ctrlKey);
    const files = Array.from(event.dataTransfer?.files || []);
    close();
    if (vm.disabled || !suppliedDrag) return;
    if (suppliedDrag.kind === 'local') {
      if (!vm.canManageParts || partsDropReason(vm.form, suppliedDrag, target, copy)) return;
      const next = resolvePartsDrop(vm.form, suppliedDrag, target, copy);
      if (next !== vm.form) vm.setField('tags', next.tags);
    } else add(files, suppliedDrag.kind === 'chat' ? suppliedDrag.payload : null, vm.canManageParts ? target : 'general');
  };
  const photoDrag = (_event, payload) => {
    if (!current.current.canManageParts || current.current.disabled) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => { timer.current = null; setDrag({ kind: 'local', ...payload }); }, 0);
  };
  current.current.drop = drop;
  useEffect(() => {
    const lifecycle = generation, pending = reservations.current;
    const node = panelRef.current?.closest('.ant-drawer-content') || panelRef.current;
    const root = node?.closest('.ant-drawer');
    if (root) setZIndex((parseInt(getComputedStyle(root).zIndex, 10) || 1000) + 100);
    const external = event => {
      const transfer = event.dataTransfer;
      let payload = activeChatMediaDrag();
      if (!payload && Array.from(transfer?.types || []).includes(CHAT_MEDIA_DRAG_TYPE)) { try { payload = readChatMediaDrag(transfer); } catch { return null; } }
      if (isImageDrag(payload)) return { kind: 'chat', payload };
      if (Array.from(transfer?.types || []).includes('Files') && (!transfer.items?.length || Array.from(transfer.items).some(item => !item.type || item.type.startsWith('image/')))) return { kind: 'files' };
      return null;
    };
    const enter = event => { const vm = current.current, item = external(event); if (item && !vm.disabled && vm.canManageParts) { event.preventDefault(); setDrag(item); } };
    const over = event => { if (!current.current.disabled && external(event)) event.preventDefault(); };
    const outsideDrop = event => {
      if (event.target.closest?.('.parts-drop-overlay')) return;
      const item = external(event);
      if (item && !current.current.canManageParts) current.current.drop(event, 'general', item);
      else close();
    };
    const key = event => { if (event.key === 'Escape') close(); };
    node?.addEventListener('dragenter', enter); node?.addEventListener('dragover', over); node?.addEventListener('drop', outsideDrop);
    window.addEventListener('dragend', close); window.addEventListener('drop', close); window.addEventListener('keydown', key);
    return () => {
      lifecycle.current++; pending.clear(); clearTimeout(timer.current);
      node?.removeEventListener('dragenter', enter); node?.removeEventListener('dragover', over); node?.removeEventListener('drop', outsideDrop);
      window.removeEventListener('dragend', close); window.removeEventListener('drop', close); window.removeEventListener('keydown', key);
    };
  }, [panelRef]); // callbacks use current form/permissions; the drawer owns this subscription
  return { drag, zIndex, photoDrag, drop, close };
}
